import * as React from 'react';
import { BREAKPOINTS } from 'PyzShopUi/scripts/config';
import { MDCTextField } from '@material/textfield';
import { createRoot, Root } from 'react-dom/client';
import MaterialDesignComponentHandler from 'PyzShopUi/scripts/modules/materialDesignComponents';
import { MIN_SEARCH_STRING_LENGTH_TO_GET_DATA } from 'PyzShopUi/scripts/search-suggestion/constants';
import { searchValueObserver } from 'PyzShopUi/scripts/search-suggestion/includes/searchSuggestionHelper';
import MiddlewareEventBus from 'PyzShopUi/scripts/react/common/lib/MiddlewareEventBus';
import IReactEvent from 'PyzShopUi/scripts/interfaces/article-list/IReactEvent';
import { WindowEvent } from 'PyzShopUi/scripts/react/common/events/windowEvent';
import SearchSuggestionContainer from 'PyzShopUi/scripts/search-suggestion/containers/SearchSuggestionContainer';

export class SearchSuggestionRendering {
    private isVisible: boolean = false;

    private searchValue: string = '';
    private isSmallScreen: boolean = BREAKPOINTS.medium();

    private readonly searchSuggestionElement: HTMLElement;
    private readonly searchInputElement: HTMLInputElement;
    private readonly searchFormElement: HTMLFormElement;
    private searchInputMDCTextField: MDCTextField;
    private searchSuggestionRoot: Root;

    constructor(searchSuggestionElement: HTMLElement, searchFormElement: HTMLFormElement) {
        this.searchSuggestionElement = searchSuggestionElement;
        this.searchFormElement = searchFormElement;
        this.searchInputElement = this.searchFormElement.querySelector('#search');
        this.searchInputMDCTextField = MaterialDesignComponentHandler.getMDCTextField(this.searchInputElement) as MDCTextField;
        this.searchValue = this.searchInputElement.value ?? '';

        this.handleLargeScreenInput();
        this.handleSmallScreenOpenDialog();

        this.handleSearchValueFromObserver();

        this.subscribeToMiddlewareEventBus();
    }

    private handleLargeScreenInput = () => {
        this.searchInputElement.addEventListener('input', (event: Event) => {
            // @ts-ignore
            const inputValue: string = event.target.value;
            this.isVisible = inputValue.trim().length >= MIN_SEARCH_STRING_LENGTH_TO_GET_DATA;

            searchValueObserver.next(inputValue);
        });

        this.searchInputElement.addEventListener('focus', (event: Event) => {
            if (BREAKPOINTS.medium()) {
                return;
            }

            // @ts-ignore
            const inputValue: string = event.target.value;
            this.isVisible = inputValue.trim().length >= MIN_SEARCH_STRING_LENGTH_TO_GET_DATA;

            if (this.isVisible) {
                this.renderComponent();
            }
        });
    };

    private handleSmallScreenOpenDialog = () => {
        this.searchInputElement.addEventListener('click', (event: Event) => {
            if (!BREAKPOINTS.medium()) {
                return;
            }
            // @ts-ignore
            const inputValue: string = event.target.value;
            this.isVisible = true;
            searchValueObserver.next(inputValue);

            this.renderComponent();
        });
    };

    private handleSearchValueFromObserver = () => {
        searchValueObserver.subscribe(observedSearchValue => {
            this.searchValue = observedSearchValue.trim();
            this.searchInputMDCTextField.value = observedSearchValue;

            this.renderComponent();
        });
    };

    private subscribeToMiddlewareEventBus = () => {
        MiddlewareEventBus.getInstance().subscribe((event: IReactEvent<any>) => {
            if (event.type !== WindowEvent.WINDOW_RESIZE_DEBOUNCED) {
                return;
            }

            this.isSmallScreen = BREAKPOINTS.medium();

            this.renderComponent();
        });
    };

    private onClose = () => {
        this.isVisible = false;

        this.renderComponent();
    };

    private renderComponent = () => {
        if (!this.searchSuggestionRoot) {
            this.searchSuggestionRoot = createRoot(this.searchSuggestionElement);
        }

        this.searchSuggestionRoot.render(
            <SearchSuggestionContainer
                searchValue={this.searchValue}
                isSmallScreen={this.isSmallScreen}
                isVisible={this.isVisible}
                minSearchStringLengthToGetData={MIN_SEARCH_STRING_LENGTH_TO_GET_DATA}
                onClose={this.onClose}
                searchUrl={this.searchFormElement.action}
            />
        );
    };
}
